import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Users, CheckCircle, Clock, TrendingUp, Filter, X } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getAssessmentById, getAssessmentAggregatedData } from '../api/services/assessmentsService';
import { useOptions } from '../contexts/OptionsContext';
import LoadingSpinner from '../components/LoadingSpinner';

function AssessmentDetail() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { departments, years, semesters, getDepartmentLabel, getYearLabel, getSemesterLabel } = useOptions();
  
  const [assessment, setAssessment] = useState(null);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [aggregatedData, setAggregatedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    department: 'All Branches',
    year: 'All Years',
    semester: 'All Semesters',
    status: 'All Status',
  });

  useEffect(() => {
    fetchAssessmentDetails();
  }, [assessmentId]);

  useEffect(() => {
    filterStudents();
  }, [searchQuery, filters, students]);

  const fetchAssessmentDetails = async () => {
    try {
      setLoading(true);
      const [assessmentResponse, aggregatedResponse] = await Promise.all([
        getAssessmentById(assessmentId),
        getAssessmentAggregatedData(assessmentId),
      ]);

      if (assessmentResponse.success && assessmentResponse.data) {
        setAssessment(assessmentResponse.data);
        // Get students from assessment data
        const assessmentStudents = assessmentResponse.data.students || [];
        // Add status and score to each student (mock data)
        const studentsWithStatus = assessmentStudents.map(student => ({
          ...student,
          status: student.status || 'Pending',
          score: student.score !== undefined ? student.score : null,
        }));
        setStudents(studentsWithStatus);
        setFilteredStudents(studentsWithStatus);
      }

      if (aggregatedResponse.success && aggregatedResponse.data) {
        setAggregatedData(aggregatedResponse.data);
      }
    } catch (error) {
      console.error('Error fetching assessment details:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterStudents = () => {
    let filtered = [...students];

    // Search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(student =>
        (student.name?.toLowerCase().includes(query)) ||
        (student.email?.toLowerCase().includes(query)) ||
        (student.id?.toLowerCase().includes(query))
      );
    }

    // Department filter
    if (filters.department !== 'All Branches') {
      filtered = filtered.filter(student =>
        (student.branch?.toLowerCase() === filters.department.toLowerCase()) ||
        (student.department?.toLowerCase() === filters.department.toLowerCase())
      );
    }

    // Year filter
    if (filters.year !== 'All Years') {
      filtered = filtered.filter(student =>
        student.year?.toLowerCase() === filters.year.toLowerCase()
      );
    }

    // Semester filter
    if (filters.semester !== 'All Semesters') {
      filtered = filtered.filter(student =>
        student.semester?.toLowerCase() === filters.semester.toLowerCase()
      );
    }

    // Status filter
    if (filters.status !== 'All Status') {
      filtered = filtered.filter(student =>
        student.status?.toLowerCase() === filters.status.toLowerCase()
      );
    }

    setFilteredStudents(filtered);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Calculate metrics from aggregated data or students
  const totalStudents = aggregatedData?.totalStudents || students.length;
  const completedCount = aggregatedData?.completedCount || students.filter(s => s.status === 'Completed').length;
  const pendingCount = aggregatedData?.pendingCount || students.filter(s => s.status === 'Pending').length;
  const completionRate = totalStudents > 0 ? (completedCount / totalStudents) * 100 : 0;
  const pendingRate = totalStudents > 0 ? (pendingCount / totalStudents) * 100 : 0;
  
  const averageScore = aggregatedData?.averageScore || (() => {
    const completedStudents = students.filter(s => s.status === 'Completed' && s.score !== null);
    return completedStudents.length > 0
      ? completedStudents.reduce((sum, s) => sum + (s.score || 0), 0) / completedStudents.length
      : 0;
  })();

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Loading assessment details..." />
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="p-8">
        <p className="text-gray-600">Assessment not found</p>
        <button
          onClick={() => navigate('/assessments')}
          className="mt-4 text-purple-600 hover:text-purple-700 cursor-pointer"
        >
          Back to Assessments
        </button>
      </div>
    );
  }

  return (
    <div className="p-8" style={{ backgroundColor: 'var(--screen-background-color)' }}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/assessments')}
        className="mb-6 text-sm font-semibold text-gray-600 hover:text-gray-800 flex items-center gap-2 cursor-pointer transition-colors"
      >
        <ArrowLeft size={16} />
        <span>Back to Assessments</span>
      </button>

      {/* Assessment Header */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {assessment.title}
            </h1>
            <p className="text-gray-600 mb-4">
              {assessment.description || 'No description'}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Language:</span>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                  {assessment.programmingLanguage || 'N/A'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Database:</span>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                  {assessment.database || 'N/A'}
                </span>
              </div>
            </div>
          </div>
          {assessment.status === 'published' && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 whitespace-nowrap">
              Published
            </span>
          )}
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Total Students */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-gray-600 text-sm mb-2">Total Students</p>
              <p className="text-3xl font-bold text-gray-900">{totalStudents}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users size={24} style={{ color: 'var(--buttons-background-color)' }} />
            </div>
          </div>
        </div>

        {/* Completed */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-gray-600 text-sm mb-2">Completed</p>
              <p className="text-3xl font-bold text-gray-900">{completedCount}</p>
              <p className="text-sm text-green-600 mt-1">{completionRate.toFixed(1)}% completion</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        {/* Pending */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-gray-600 text-sm mb-2">Pending</p>
              <p className="text-3xl font-bold text-gray-900">{pendingCount}</p>
              <p className="text-sm text-orange-600 mt-1">{pendingRate.toFixed(1)}% pending</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Clock size={24} className="text-orange-600" />
            </div>
          </div>
        </div>

        {/* Average Score */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-gray-600 text-sm mb-2">Average Score</p>
              <p className="text-3xl font-bold text-gray-900">{averageScore.toFixed(1)}%</p>
              <p className="text-sm text-gray-600 mt-1">of {assessment.totalMarks || 100} marks</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingUp size={24} className="text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Completion Status Chart */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-6">
            <Clock size={20} className="text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-800">Completion Status</h2>
          </div>
          {aggregatedData?.completionStatus ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={aggregatedData.completionStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {aggregatedData.completionStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No data available
            </div>
          )}
        </div>

        {/* Score Distribution Chart */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp size={20} className="text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-800">Score Distribution</h2>
          </div>
          {aggregatedData?.scoreDistribution ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={aggregatedData.scoreDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="range" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  label={{ value: 'Number of Students', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]}>
                  {aggregatedData.scoreDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.count > 0 ? '#3b82f6' : '#e5e7eb'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Students Table Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Students</h2>
          
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search students in this group..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-sm text-gray-800"
              />
            </div>
            <div className="flex items-center gap-3">
              <Filter size={18} className="text-gray-400" />
              <select
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
                className="px-3 py-2 border text-gray-600 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none cursor-pointer bg-white"
              >
                {departments.map(dept => (
                  <option key={dept.value} value={dept.value}>{dept.label}</option>
                ))}
              </select>
              <select
                value={filters.year}
                onChange={(e) => handleFilterChange('year', e.target.value)}
                className="px-3 py-2 border text-gray-600 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none cursor-pointer bg-white"
              >
                {years.map(year => (
                  <option key={year.value} value={year.value}>{year.label}</option>
                ))}
              </select>
              <select
                value={filters.semester}
                onChange={(e) => handleFilterChange('semester', e.target.value)}
                className="px-3 py-2 border text-gray-600 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none cursor-pointer bg-white"
              >
                <option value="All Semesters">All Semesters</option>
                {semesters.filter(s => s.value !== 'All Semesters').map(sem => (
                  <option key={sem.value} value={sem.value}>{sem.label}</option>
                ))}
              </select>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="px-3 py-2 border text-gray-600 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none cursor-pointer bg-white"
              >
                <option value="All Status">All Status</option>
                <option value="Completed">Completed</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
          </div>

          {/* Active Filter Chips */}
          {(searchQuery || filters.department !== 'All Branches' || filters.year !== 'All Years' || filters.semester !== 'All Semesters' || filters.status !== 'All Status') && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="text-sm text-gray-600 font-medium">Active filters:</span>
              
              {/* Search Query Chip */}
              {searchQuery && (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: 'var(--buttons-background-color)' }}
                >
                  <span>Search: "{searchQuery}"</span>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="hover:bg-white/20 rounded-full p-0.5 transition-colors cursor-pointer"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Department Filter Chip */}
              {filters.department !== 'All Branches' && (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: 'var(--buttons-background-color)' }}
                >
                  <span>Branch: {getDepartmentLabel(filters.department)}</span>
                  <button
                    onClick={() => handleFilterChange('department', 'All Branches')}
                    className="hover:bg-white/20 rounded-full p-0.5 transition-colors cursor-pointer"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Year Filter Chip */}
              {filters.year !== 'All Years' && (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: 'var(--buttons-background-color)' }}
                >
                  <span>Year: {getYearLabel(filters.year)}</span>
                  <button
                    onClick={() => handleFilterChange('year', 'All Years')}
                    className="hover:bg-white/20 rounded-full p-0.5 transition-colors cursor-pointer"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Semester Filter Chip */}
              {filters.semester !== 'All Semesters' && (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: 'var(--buttons-background-color)' }}
                >
                  <span>Semester: {getSemesterLabel(filters.semester)}</span>
                  <button
                    onClick={() => handleFilterChange('semester', 'All Semesters')}
                    className="hover:bg-white/20 rounded-full p-0.5 transition-colors cursor-pointer"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Status Filter Chip */}
              {filters.status !== 'All Status' && (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: 'var(--buttons-background-color)' }}
                >
                  <span>Status: {filters.status}</span>
                  <button
                    onClick={() => handleFilterChange('status', 'All Status')}
                    className="hover:bg-white/20 rounded-full p-0.5 transition-colors cursor-pointer"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Clear All Button */}
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilters({
                    department: 'All Branches',
                    year: 'All Years',
                    semester: 'All Semesters',
                    status: 'All Status',
                  });
                }}
                className="text-sm text-gray-600 hover:text-gray-800 underline cursor-pointer"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Students Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  STUDENT ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  NAME
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  BRANCH
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  YEAR
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  SEMESTER
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  STATUS
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  SCORE
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    {searchQuery || filters.department !== 'All Branches' || filters.year !== 'All Years' || filters.semester !== 'All Semesters' || filters.status !== 'All Status'
                      ? 'No students found matching your filters'
                      : 'No students assigned to this assessment'}
                  </td>
                </tr>
              ) : (
                filteredStudents.map((student, index) => (
                  <tr key={student.id || index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {student.id || student.student_id || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div>
                        <p className="font-medium text-gray-900">{student.name || 'N/A'}</p>
                        <p className="text-gray-500 text-xs">{student.email || ''}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {student.branch || student.department || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {student.year || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {student.semester || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {student.status === 'Completed' ? (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                          Completed
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {student.score !== null && student.score !== undefined ? `${student.score}%` : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AssessmentDetail;

